import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ShoppingCart, Trash2, ArrowRight, Package, Ticket, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackingTimeoutRef = useRef(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Fonction pour tracker le panier (relance abandonnée)
  const trackCart = useCallback(async (items) => {
    if (items.length === 0) return;
    
    try {
      const trackingItems = items.map(item => ({
        listing_id: item.id,
        title: item.title,
        price: item.price || 0,
        image: item.images?.[0] || ''
      }));

      await axios.post(`${API}/cart/track`, {
        items: trackingItems,
        email: user?.email || null
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      // Silently fail - tracking should not impact user experience
      console.debug('Cart tracking:', error.message);
    }
  }, [user, token]);

  // Debounced tracking - track cart 5 seconds after last change
  const scheduleTracking = useCallback((items) => {
    if (trackingTimeoutRef.current) {
      clearTimeout(trackingTimeoutRef.current);
    }
    trackingTimeoutRef.current = setTimeout(() => {
      trackCart(items);
    }, 5000);
  }, [trackCart]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('worldauto_cart');
    if (savedCart) {
      const items = JSON.parse(savedCart);
      setCartItems(items);
      // Fetch latest listing info for each item
      fetchListingDetails(items);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchListingDetails = async (items) => {
    try {
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          try {
            const response = await axios.get(`${API}/listings/${item.id}`);
            return { ...item, ...response.data, available: true };
          } catch {
            return { ...item, available: false };
          }
        })
      );
      const validItems = updatedItems.filter(item => item.available);
      setCartItems(validItems);
      // Update localStorage with valid items only
      localStorage.setItem('worldauto_cart', JSON.stringify(validItems));
      // Track cart for abandoned cart recovery
      if (validItems.length > 0) {
        scheduleTracking(validItems);
      }
    } catch (error) {
      console.error('Error fetching cart items:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = (listingId) => {
    const updatedCart = cartItems.filter(item => item.id !== listingId);
    setCartItems(updatedCart);
    localStorage.setItem('worldauto_cart', JSON.stringify(updatedCart));
    // Re-track with updated cart
    if (updatedCart.length > 0) {
      scheduleTracking(updatedCart);
    }
    toast.success('Article retiré du panier');
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('worldauto_cart');
    setAppliedCoupon(null);
    setCouponCode('');
    toast.success('Panier vidé');
  };

  // Apply coupon
  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    try {
      const response = await axios.post(
        `${API}/coupons/validate`,
        null,
        { 
          params: { code: couponCode, cart_total: totalPrice },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setAppliedCoupon(response.data);
      toast.success(`Code promo "${response.data.code}" appliqué !`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code promo invalide');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.success('Code promo retiré');
  };

  const totalPrice = cartItems.reduce((sum, item) => sum + (item.price || 0), 0);
  const finalPrice = appliedCoupon ? appliedCoupon.new_total : totalPrice;

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="h-32 bg-muted rounded"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30 py-8">
      <SEO
        title={t('cart.title')}
        description="Votre panier d'achats sur World Auto Pro Pro"
        url="/panier"
        noindex={true}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <ShoppingCart className="w-8 h-8" />
            {t('cart.title')}
          </h1>
          {cartItems.length > 0 && (
            <Button variant="outline" onClick={clearCart} className="text-destructive">
              <Trash2 className="w-4 h-4 mr-2" />
              {t('cart.clear')}
            </Button>
          )}
        </div>

        {cartItems.length === 0 ? (
          <Card className="p-12 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-heading text-xl font-semibold mb-2">{t('cart.empty')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('cart.empty_desc')}
            </p>
            <Link to="/annonces">
              <Button className="bg-accent hover:bg-accent/90">
                {t('cart.browse')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Cart Items */}
            <div className="space-y-4">
              {cartItems.map((item) => (
                <Card key={item.id} className="p-4">
                  <div className="flex gap-4">
                    <Link to={`/annonce/${item.id}`} className="flex-shrink-0">
                      <img
                        src={item.images?.[0] || '/placeholder.jpg'}
                        alt={item.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/annonce/${item.id}`}>
                        <h3 className="font-heading font-semibold hover:text-accent transition-colors truncate">
                          {item.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground mt-1">
                        {item.category} • {item.condition}
                      </p>
                      <p className="font-heading text-xl font-bold text-accent mt-2">
                        {item.price?.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="flex items-start">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromCart(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Summary */}
            <Card className="p-6">
              {/* Coupon Input */}
              <div className="mb-6 pb-6 border-b">
                <label className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Ticket className="w-4 h-4" />
                  Code promo
                </label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="font-mono font-bold text-green-700">{appliedCoupon.code}</span>
                        <span className="text-sm text-green-600 ml-2">
                          (-{appliedCoupon.discount_amount.toLocaleString('fr-FR')} €)
                        </span>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={removeCoupon}
                      className="text-green-600 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Entrez votre code"
                      className="font-mono uppercase"
                      onKeyPress={(e) => e.key === 'Enter' && applyCoupon()}
                    />
                    <Button 
                      onClick={applyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      variant="outline"
                    >
                      {couponLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Appliquer'
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Sous-total ({cartItems.length} article{cartItems.length > 1 ? 's' : ''})</span>
                  <span className="font-medium">{totalPrice.toLocaleString('fr-FR')} €</span>
                </div>
                
                {appliedCoupon && (
                  <div className="flex items-center justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <Ticket className="w-4 h-4" />
                      Réduction ({appliedCoupon.code})
                    </span>
                    <span className="font-medium">-{appliedCoupon.discount_amount.toLocaleString('fr-FR')} €</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="font-heading text-2xl font-bold text-accent">
                    {finalPrice.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-6">
                <Package className="w-4 h-4 inline mr-1" />
                Frais de livraison à convenir avec les vendeurs
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/checkout">
                  <Button className="w-full h-12 bg-accent hover:bg-accent/90 text-lg">
                    Passer commande
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/annonces">
                  <Button variant="outline" className="w-full">
                    Continuer mes achats
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
